import React, { useMemo, useState } from "react";
import { formatToLocalTime } from "../utils/dateUtils";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { tr, enUS } from "date-fns/locale";
import { addHours } from "date-fns";
import { trLocaleText, enLocaleText } from "../utils/datePickerLocale";
import ResponsiveSelect from "../components/ResponsiveSelect";
import ClearIcon from "@mui/icons-material/Clear";
import useTriStateSort from "../hooks/useTriStateSort";
import PageHeader from "../components/PageHeader";
import { Alert, Box, Button, CircularProgress, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField, Toolbar, Tooltip, useMediaQuery, } from "@mui/material";
import SearchField from "../components/SearchField";
import { gql, useQuery } from "@apollo/client";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";
import EllipsisCell from "../components/EllipsisCell";
import TablePaginationActions from "../components/TablePaginationActions";


const FILTER_RESELLERS = gql`
    query FilterReseller {
        filterReseller(criteria: { deleted: false }, sortBy: { field: "name", direction: ASC }) {
            id
            name
        }
    }
`;

const FILTER_LOCATIONS = gql`
    query FilterLocation {
        filterLocation(criteria: { deleted: false }, sortBy: { field: "name", direction: ASC }) {
            id
            name
        }
    }
`;

const FILTER_CUSTOMERS = gql`
    query FilterCustomer {
        filterCustomer(
            criteria: { deleted: false }
            sortBy: { field: "name", direction: ASC }
        ) {
            id
            name
        }
    }
`;
const DeviceStatisticTemplate = ({ title, subtitle, query, columns, icon, isAlarmPage }) => {
    const { t, i18n } = useTranslation();
    const isSmall = useMediaQuery("(max-width:600px)");
    const role = localStorage.getItem("role") || sessionStorage.getItem("role");


    const [page, setPage] = useState(0);
    const [rowsPerPage, setRpp] = useState(10);
    const { sortFields, handleSort, getSortProps } = useTriStateSort(
        (updated) => {
            refetch({ ...variables, sortBy: updated });
        }
    );

    const [filters, setFilters] = useState({
        serialNumber: "",
        location: "",
        reseller: "",
        device: "",
        customer: "",
        startDateTime: "",
        endDateTime: "",
        alarmType: "",
        deviceType: ""
    });
    const [debouncedFilters, setDebouncedFilters] = useState(filters);


    const { data: resellerData } = useQuery(FILTER_RESELLERS, { fetchPolicy: "no-cache" });
    const { data: locationData } = useQuery(FILTER_LOCATIONS, { fetchPolicy: "no-cache" });
    const { data: customerData } = useQuery(FILTER_CUSTOMERS, { fetchPolicy: "no-cache" });
    const applyFilters = () => {
        setDebouncedFilters(filters);
        setPage(0);
    };

    const resetFilters = () => {
        const cleared = {
            serialNumber: "",
            location: "",
            reseller: "",
            device: "",
            customer: "",
            startDateTime: "",
            endDateTime: "",
        };
        setFilters(cleared);
        setDebouncedFilters(cleared);
        setPage(0);
    };

    const variables = useMemo(() => {
        const criteria = {
            ...(debouncedFilters.serialNumber && { serialNumber: debouncedFilters.serialNumber }),
            ...(debouncedFilters.location && { location: debouncedFilters.location }),
            ...(debouncedFilters.reseller && { reseller: debouncedFilters.reseller }),
            ...(debouncedFilters.device && { device: debouncedFilters.device }),
            ...(debouncedFilters.startDateTime && { startDateTime: addHours(debouncedFilters.startDateTime, 3) }),
            ...(debouncedFilters.endDateTime && { endDateTime: addHours(debouncedFilters.endDateTime, 3) }),
            ...(debouncedFilters.customer && { customer: debouncedFilters.customer }),
            ...(isAlarmPage && debouncedFilters.alarmType && { type: debouncedFilters.alarmType }),
            ...(isAlarmPage && debouncedFilters.deviceType && { deviceType: debouncedFilters.deviceType }),
        };
        return {
            pagination: { page, itemsPerPage: rowsPerPage },
            sortBy: sortFields,
            criteria,
        };
    }, [page, rowsPerPage, sortFields, debouncedFilters]);

    const { data, loading, error, refetch } = useQuery(query, {
        variables,
        fetchPolicy: "no-cache",
    });

    const rootKey = Object.keys(data || {})[0];
    const pageInfo = data?.[rootKey]?.pageInfo;
    const rows = data?.[rootKey]?.content || [];

    const visibleColumns = useMemo(() => {
        if (role === "RESELLER" || role === "CUSTOMER") {
            return columns.filter((c) => c.field.toLowerCase() !== "resellername");
        }
        return columns;
    }, [role, columns]);

    const handleChangePage = (_e, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRpp(parseInt(e.target.value, 10));
        setPage(0);
    };


    return (
        <Box>

            <PageHeader
                title={title}
                subtitle={subtitle}
                icon={icon}
            />

            <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>

                <Toolbar
                    sx={{
                        px: 2,
                        py: 1,
                        gap: 1,
                        flexWrap: "wrap",
                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                        bgcolor: "background.paper",
                    }}
                >

                    <SearchField
                        label={t("statisticPage.fields.serialNumber")}
                        value={filters.serialNumber}
                        onChange={(e) => setFilters((p) => ({ ...p, serialNumber: e.target.value }))}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                setDebouncedFilters((prev) => ({ ...prev, serialNumber: e.target.value }));
                                setPage(0);

                            }
                        }}
                        placeholder={t("statisticPage.search.serialPlaceholder")}
                        isSmall={isSmall}
                    />

                    {isAlarmPage && (
                        <ResponsiveSelect
                            label={t("alarm.columns.deviceType")}
                            value={filters.deviceType}
                            onChange={(e) => setFilters((p) => ({ ...p, deviceType: e.target.value }))}
                            onClear={() => setFilters((p) => ({ ...p, deviceType: "" }))}
                            options={[
                                { value: "MAKMINI", label: t("menu.makmini") },
                                { value: "AKIFERRA", label: t("menu.akiferra") }
                            ]}
                            t={t}
                            isSmall={isSmall}
                        />
                    )}

                    {isAlarmPage && (
                        <ResponsiveSelect
                            label={t("alarm.filters.alarmType")}
                            value={filters.alarmType}
                            onChange={(e) => setFilters((p) => ({ ...p, alarmType: e.target.value }))}
                            onClear={() => setFilters((p) => ({ ...p, alarmType: "" }))}
                            options={[
                                { value: "HIGH_EC", label: t("alarm.types.HIGH_EC") },
                                { value: "LOW_EC", label: t("alarm.types.LOW_EC") },
                                { value: "HIGH_TEMP", label: t("alarm.types.HIGH_TEMP") },
                                { value: "LOW_TEMP", label: t("alarm.types.LOW_TEMP") },
                                { value: "FLOW", label: t("alarm.types.FLOW") }
                            ]}
                            t={t}
                            isSmall={isSmall}
                        />
                    )}

                    {role !== "CUSTOMER" && role !== "RESELLER" && (

                        <ResponsiveSelect
                            label={t("statisticPage.fields.reseller")}
                            value={filters.reseller}
                            onChange={(e) => setFilters((p) => ({ ...p, reseller: e.target.value }))}
                            onClear={() => setFilters((p) => ({ ...p, reseller: "" }))}
                            data={resellerData}
                            dataKey="filterReseller"
                            t={t}
                            isSmall={isSmall}
                        />

                    )}


                    {role !== "CUSTOMER" && (


                        <ResponsiveSelect
                            label={t("statisticPage.fields.customer")}
                            value={filters.customer}
                            onChange={(e) => setFilters((p) => ({ ...p, customer: e.target.value }))}
                            onClear={() => setFilters((p) => ({ ...p, customer: "" }))}
                            data={customerData}
                            dataKey="filterCustomer"
                            t={t}
                            isSmall={isSmall}
                        />
                    )}

                    <ResponsiveSelect
                        label={t("statisticPage.fields.location")}
                        value={filters.location}
                        onChange={(e) => setFilters((p) => ({ ...p, location: e.target.value }))}
                        onClear={() => setFilters((p) => ({ ...p, location: "" }))}
                        data={locationData}
                        dataKey="filterLocation"
                        t={t}
                        isSmall={isSmall}
                    />
                    
                    <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={i18n.language.startsWith("tr") ? tr : enUS}
                        localeText={i18n.language.startsWith("tr") ? trLocaleText : enLocaleText}
                    >

                        <DateTimePicker
                            label={t("statisticPage.fields.startDate")}
                            value={filters.startDateTime ? new Date(filters.startDateTime) : null}
                            onChange={(newValue) =>
                                setFilters((p) => ({ ...p, startDateTime: newValue }))
                            }

                            slotProps={{
                                textField: {
                                    size: "small",
                                    sx: {
                                        width: 210,
                                        "& .MuiInputBase-root": {
                                            height: 30,
                                            fontSize: "0.75rem",
                                            padding: "0 6px",
                                        },
                                        "& .MuiInputLabel-root": {
                                            fontSize: "1rem",
                                            top: "-2px",
                                        },
                                        "& .MuiSvgIcon-root": {
                                            fontSize: "0.85rem",
                                        },
                                        "& .MuiInputAdornment-root svg": {
                                            fontSize: "0.85rem",
                                        },
                                    },
                                },
                            }}
                        />

                        <DateTimePicker
                            label={t("statisticPage.fields.endDate")}
                            value={filters.endDateTime ? new Date(filters.endDateTime) : null}
                            onChange={(newValue) =>
                                setFilters((p) => ({ ...p, endDateTime: newValue }))
                            }
                            slotProps={{
                                textField: {
                                    size: "small",
                                    sx: {
                                        width: 200,
                                        "& .MuiInputBase-root": {
                                            height: 30,
                                            fontSize: "0.75rem",
                                            padding: "0 6px",
                                        },
                                        "& .MuiInputLabel-root": {
                                            fontSize: "1rem",
                                            top: "-2px",
                                        },
                                        "& .MuiSvgIcon-root": {
                                            fontSize: "0.85rem",
                                        },
                                        "& .MuiInputAdornment-root svg": {
                                            fontSize: "0.85rem",
                                        },
                                    },
                                },
                            }}
                        />

                    </LocalizationProvider>


                    <Button
                        variant="contained"
                        startIcon={<SearchIcon />}
                        onClick={applyFilters}
                        sx={{
                            height: 40,
                            px: 2,
                            textTransform: "none",
                            fontWeight: 500,
                            minWidth: isSmall ? "100%" : "auto",
                        }}
                    >
                        {t("common.search")}
                    </Button>

                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<ClearIcon />}
                        onClick={() => {
                            resetFilters();

                        }}
                        sx={{
                            height: 40,
                            px: 2,
                            textTransform: "none",
                            fontWeight: 500,
                            minWidth: isSmall ? "100%" : "auto",
                        }}
                    >
                        {t("common.clear")}
                    </Button>
                    <Box sx={{ flex: 1 }} />


                    <Tooltip title={t("common.refresh")}>
                        <IconButton onClick={() => refetch()} aria-label="refresh">
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Toolbar>


                {loading ? (
                    <Box sx={{ p: 4, textAlign: "center" }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box sx={{ p: 2 }}>
                        <Alert severity="error">{error.message}</Alert>
                    </Box>
                ) : (
                    <>
                        <TableContainer sx={{
                            maxHeight: 520,
                            overflowX: "auto",
                            overflowY: "auto",
                            position: "relative",
                        }}>
                            <Table stickyHeader
                                sx={{
                                    minWidth: visibleColumns.length * 150,
                                    tableLayout: "fixed",
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        {visibleColumns.map((col) => (
                                            <EllipsisCell
                                                key={col.field}
                                                sx={{
                                                    bgcolor: "grey.50",
                                                    fontWeight: 600,
                                                    fontSize: "0.875rem",
                                                    borderBottom: "2px solid",
                                                    borderBottomColor: "divider",
                                                    color: "text.primary",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",

                                                    width: col.width ?? 150,
                                                    minWidth: col.width ?? 150,
                                                    maxWidth: col.width ?? 150,

                                                    // position: col.sticky === "right" ? "sticky" : "static",
                                                    right: col.sticky === "right" ? 0 : "auto",
                                                    zIndex: col.sticky === "right" ? 7 : 1,
                                                }}
                                            >
                                                {col.sortable ? (
                                                    <TableSortLabel
                                                        {...getSortProps(col.field)}

                                                        onClick={() => handleSort(col.field)}
                                                    >
                                                        {col.label}
                                                    </TableSortLabel>
                                                ) : (
                                                    col.label
                                                )}
                                            </EllipsisCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.length === 0 ? (
                                        <TableRow>
                                            <EllipsisCell colSpan={visibleColumns.length} align="center">
                                                {t("statisticPage.noRecords")}
                                            </EllipsisCell>
                                        </TableRow>
                                    ) : (
                                        rows.map((r) => (
                                            <TableRow
                                                key={r.id}
                                                hover
                                                sx={{
                                                    "&:nth-of-type(even)": {
                                                        bgcolor: "grey.50"
                                                    },
                                                    "&:hover": {
                                                        bgcolor: "action.hover"
                                                    }
                                                }}
                                            >
                                                {visibleColumns.map((col) => (
                                                    <EllipsisCell
                                                        key={col.field}
                                                        sticky={col.sticky}
                                                        maxWidth={col.width}
                                                        nowrap={col.nowrap}

                                                    >
                                                        {col.format ? col.format(r[col.field], r) : r[col.field] ?? "-"}
                                                    </EllipsisCell>

                                                ))}
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            labelRowsPerPage={t("common.rowsPerPage")}
                            component="div"
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            count={pageInfo?.totalElements ?? 0}
                            rowsPerPage={pageInfo?.pageSize ?? rowsPerPage}
                            page={pageInfo?.pageNumber ?? page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                        />
                    </>
                )}
            </Paper>
        </Box >
    );
};

export default DeviceStatisticTemplate;
